import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtNum, fmtEur, fmtKg, fmtMonat, downloadBlob, errMsg, letzteMonate } from "@/lib/api";
import { EmptyState } from "@/components/app/EmptyState";
import { QualityBadge } from "@/components/app/QualityBadge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Receipt, Loader2, FileDown, Eye, Info } from "lucide-react";
import { toast } from "sonner";

export const AbrechnungDetailDialog = ({ abrechnung, onClose }) => {
  const [pdfLoading, setPdfLoading] = useState(false);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      await downloadBlob(`/abrechnungen/${abrechnung.id}/pdf`, `Milchgeld_${abrechnung.liefernummer}_${abrechnung.monat}.pdf`);
      toast.success("PDF heruntergeladen.");
    } catch (e) {
      toast.error(errMsg(e, "PDF konnte nicht erstellt werden."));
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <Dialog open={!!abrechnung} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="settlement-detail-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Milchgeldabrechnung {fmtMonat(abrechnung?.monat)}</DialogTitle>
          <DialogDescription>
            Liefernummer {abrechnung?.liefernummer} · {abrechnung?.lieferant_name} {abrechnung?.hofname ? `· ${abrechnung.hofname}` : ""}
          </DialogDescription>
        </DialogHeader>
        {abrechnung && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Menge</p><p className="font-num font-semibold">{fmtKg(abrechnung.menge_kg)}</p></div>
              <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Auszahlungspreis</p><p className="font-num font-semibold">{fmtNum(abrechnung.cent_je_kg, 3)} ct/kg</p></div>
              <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Qualität</p><QualityBadge klasse={abrechnung.qualitaetsklasse} icon={abrechnung.qualitaetsklasse_icon} farbe={abrechnung.qualitaetsklasse_farbe} showText /></div>
              <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Milchsorte</p><p className="text-sm font-medium">{abrechnung.milchsorte_name}</p></div>
            </div>

            <div className="rounded-lg border overflow-hidden">
              <Table data-testid="settlement-positions-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Position</TableHead>
                    <TableHead className="text-right">ct/kg</TableHead>
                    <TableHead className="text-right">Betrag</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {abrechnung.positionen?.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm">{p.bezeichnung}</TableCell>
                      <TableCell className={`text-right font-num ${p.cent_je_kg < 0 ? "text-destructive" : ""}`}>
                        {p.cent_je_kg > 0 ? "+" : ""}{fmtNum(p.cent_je_kg, 3)}
                      </TableCell>
                      <TableCell className={`text-right font-num ${p.betrag_eur < 0 ? "text-destructive" : ""}`}>{fmtEur(p.betrag_eur)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span>Nettobetrag</span><span className="font-num font-medium">{fmtEur(abrechnung.netto_eur)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Umsatzsteuer {fmtNum(abrechnung.ust_satz)} %</span><span className="font-num">{fmtEur(abrechnung.ust_eur)}</span></div>
              <Separator />
              <div className="flex justify-between text-base font-semibold"><span>Auszahlungsbetrag</span><span className="font-num" data-testid="settlement-brutto-amount">{fmtEur(abrechnung.brutto_eur)}</span></div>
            </div>

            <div className="text-xs text-muted-foreground">
              Geometrische Mittel: Keimzahl {fmtNum(abrechnung.keimzahl_geo)} (2 Monate) · Zellzahl {fmtNum(abrechnung.zellzahl_geo)} (3 Monate)
              {abrechnung.preismodell && <span> · Preismodell: {abrechnung.preismodell}</span>}
              {abrechnung.hemmstoff_positiv && <span className="text-destructive font-medium"> · Hemmstoff positiv</span>}
            </div>

            {(abrechnung.zellzahl_ausreisser?.length > 0 || abrechnung.keimzahl_ausreisser?.length > 0) && (
              <div data-testid="settlement-outlier-note" className="rounded-lg border border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/5 p-3 space-y-1">
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <Info className="h-4 w-4 text-[hsl(var(--info))]" /> Ausreißer-Regelung angewendet
                </p>
                {abrechnung.zellzahl_ausreisser?.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Zellzahl: Einzelwert(e) {abrechnung.zellzahl_ausreisser.map((a) => fmtNum(a)).join(", ")} unberücksichtigt –
                    geom. Mittel {fmtNum(abrechnung.zellzahl_geo_roh)} → {fmtNum(abrechnung.zellzahl_geo)}.
                  </p>
                )}
                {abrechnung.keimzahl_ausreisser?.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Keimzahl: Einzelwert(e) {abrechnung.keimzahl_ausreisser.map((a) => fmtNum(a)).join(", ")} unberücksichtigt –
                    geom. Mittel {fmtNum(abrechnung.keimzahl_geo_roh)} → {fmtNum(abrechnung.keimzahl_geo)}.
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground">
                  Gemäß güterechtlichen Bestimmungen bleibt ein einzelner, auf ein einmaliges Ereignis
                  zurückzuführender Extremwert bei der Einstufung unberücksichtigt.
                </p>
              </div>
            )}

            <Button data-testid="invoice-pdf-download-button" onClick={downloadPdf} disabled={pdfLoading} className="w-full">
              {pdfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
              PDF herunterladen
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function AbrechnungenPage() {
  const { user, liefernummer } = useAuth();
  const [rows, setRows] = useState([]);
  const [uebersicht, setUebersicht] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [suche, setSuche] = useState("");
  const [monatFilter, setMonatFilter] = useState("alle");
  // Default: nur aktive Buchungen (freigegeben + entwurf).
  // Storno + Gegenbuchung sind buchhalterisch immer als negative Paare
  // gespeichert und werden nur bei Bedarf via Toggle sichtbar.
  const [statusFilter, setStatusFilter] = useState("aktiv");

  const istLieferant = user?.rolle === "lieferant";

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (istLieferant && liefernummer) params.liefernummer = liefernummer;
    if (monatFilter !== "alle") params.monat = monatFilter;
    if (!istLieferant && statusFilter !== "alle") params.status = statusFilter;
    const promises = [api.get("/abrechnungen", { params })];
    if (!istLieferant) promises.push(api.get("/abrechnungen/uebersicht", { params: { monate: 12 } }));
    Promise.all(promises)
      .then(([abr, ueb]) => {
        setRows(abr.data);
        if (ueb) setUebersicht(ueb.data);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [istLieferant, liefernummer, monatFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const gefiltert = suche.trim()
    ? rows.filter((r) =>
        r.liefernummer.includes(suche.trim()) ||
        (r.lieferant_name || "").toLowerCase().includes(suche.trim().toLowerCase()))
    : rows;

  // Nach Monaten gruppieren
  const gruppiert = useMemo(() => {
    const groups = {};
    for (const r of gefiltert) {
      groups[r.monat] = groups[r.monat] || [];
      groups[r.monat].push(r);
    }
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [gefiltert]);

  const STATUS_LABELS = {
    entwurf: "Entwurf", freigegeben: "Freigegeben",
    storniert: "Storniert", gegenbuchung: "Gegenbuchung",
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Abrechnungen</h1>
          <p className="text-sm text-muted-foreground">
            {istLieferant ? `Liefernummer ${liefernummer}` : "Alle Lieferanten"} · Milchgeldabrechnungen mit Positionsaufstellung
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={monatFilter} onValueChange={setMonatFilter}>
            <SelectTrigger data-testid="settlements-month-filter" className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle Monate</SelectItem>
              {letzteMonate(12).map((m) => <SelectItem key={m} value={m}>{fmtMonat(m)}</SelectItem>)}
            </SelectContent>
          </Select>
          {!istLieferant && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="settlements-status-filter" className="w-[190px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="aktiv">Aktive Buchungen</SelectItem>
                <SelectItem value="entwurf">Nur Entwurf</SelectItem>
                <SelectItem value="freigegeben">Nur Freigegeben</SelectItem>
                <SelectItem value="alle">Alle (inkl. Stornos)</SelectItem>
                <SelectItem value="storniert">Nur Storno-Originale</SelectItem>
                <SelectItem value="gegenbuchung">Nur Gegenbuchungen</SelectItem>
              </SelectContent>
            </Select>
          )}
          {!istLieferant && (
            <Input
              data-testid="settlements-search-input"
              placeholder="Suche Liefernummer oder Name …"
              className="w-[220px]"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
            />
          )}
        </div>
      </div>

      {!istLieferant && uebersicht.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Monatsübersicht</CardTitle>
            <CardDescription>Kennzahlen der letzten 12 Monate (Summen nur aus freigegebenen Abrechnungen)</CardDescription>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table data-testid="settlements-overview-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Monat</TableHead>
                  <TableHead className="text-right">Entwurf</TableHead>
                  <TableHead className="text-right">Freigegeben</TableHead>
                  <TableHead className="text-right">Storniert</TableHead>
                  <TableHead className="text-right">Menge (kg)</TableHead>
                  <TableHead className="text-right">Netto (€)</TableHead>
                  <TableHead className="text-right">Brutto (€)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uebersicht.slice().reverse().map((m) => (
                  <TableRow key={m.monat} className="cursor-pointer hover:bg-accent/40"
                    onClick={() => setMonatFilter(m.monat)}>
                    <TableCell className="font-medium">{fmtMonat(m.monat)}</TableCell>
                    <TableCell className="text-right font-num">{fmtNum(m.entwurf)}</TableCell>
                    <TableCell className="text-right font-num text-[hsl(var(--success))]">{fmtNum(m.freigegeben)}</TableCell>
                    <TableCell className="text-right font-num text-muted-foreground">{fmtNum(m.storniert)}</TableCell>
                    <TableCell className="text-right font-num">{fmtNum(m.menge_kg)}</TableCell>
                    <TableCell className="text-right font-num">{fmtEur(m.netto_eur)}</TableCell>
                    <TableCell className="text-right font-num font-semibold">{fmtEur(m.brutto_eur)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : gefiltert.length ? (
            <div className="space-y-4">
              {gruppiert.map(([m, list]) => {
                const summeMenge = list.filter(x => x.status === "freigegeben").reduce((a, r) => a + (r.menge_kg || 0), 0);
                const summeNetto = list.filter(x => x.status === "freigegeben").reduce((a, r) => a + (r.netto_eur || 0), 0);
                return (
                <div key={m} className="border-b last:border-0" data-testid={`settlements-month-group-${m}`}>
                  <div className="flex items-center justify-between px-4 py-2 bg-muted/40 border-b">
                    <div className="font-semibold text-sm">{fmtMonat(m)}</div>
                    <div className="text-xs text-muted-foreground font-num">
                      {list.length} Einträge · {fmtNum(summeMenge)} kg · {fmtEur(summeNetto)} netto
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {!istLieferant && <TableHead>Liefernr.</TableHead>}
                          {!istLieferant && <TableHead>Lieferant</TableHead>}
                          <TableHead className="text-right">Menge (kg)</TableHead>
                          <TableHead className="text-right">ct/kg</TableHead>
                          <TableHead>Qualität</TableHead>
                          {!istLieferant && <TableHead>Status</TableHead>}
                          <TableHead className="text-right">Brutto</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {list.map((r) => (
                          <TableRow key={r.id} className={`cursor-pointer ${r.status === "storniert" ? "opacity-60" : ""}`} onClick={() => setDetail(r)}>
                            {!istLieferant && <TableCell className="font-num">{r.liefernummer}</TableCell>}
                            {!istLieferant && <TableCell className="text-sm">{r.lieferant_name} <span className="text-muted-foreground">v{r.version}</span></TableCell>}
                            <TableCell className="text-right font-num">{fmtNum(r.menge_kg)}</TableCell>
                            <TableCell className="text-right font-num">{fmtNum(r.cent_je_kg, 2)}</TableCell>
                            <TableCell><QualityBadge klasse={r.qualitaetsklasse} icon={r.qualitaetsklasse_icon} farbe={r.qualitaetsklasse_farbe} /></TableCell>
                            {!istLieferant && (
                              <TableCell>
                                <Badge variant={r.status === "freigegeben" ? "default" : "secondary"}
                                  className={r.status === "freigegeben" ? "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]" : ""}
                                  data-testid={`settlement-status-${r.liefernummer}`}>
                                  {STATUS_LABELS[r.status] || r.status}
                                </Badge>
                              </TableCell>
                            )}
                            <TableCell className="text-right font-num font-semibold">{fmtEur(r.brutto_eur)}</TableCell>
                            <TableCell>
                              <Button data-testid={`settlement-open-button-${r.liefernummer}-${r.monat}`} variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDetail(r); }}>
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <EmptyState icon={Receipt} title="Noch keine Abrechnungen vorhanden." text={istLieferant ? "Abrechnungen werden angezeigt, sobald sie von der Molkerei freigegeben wurden." : "Abrechnungen werden nach dem monatlichen Abrechnungslauf angezeigt."} />
          )}
        </CardContent>
      </Card>

      <AbrechnungDetailDialog abrechnung={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
